import {
  selectMemberInfos,
  selectToken,
} from "@/store/reducers/member/member.selector";
import {
  RouteObject,
  useLocation,
  useNavigate,
  useRoutes,
} from "react-router-dom";
import { publicRoutes } from "./public.routes";
import { protectedRoutes } from "./protected.routes";
import { routePaths, SOCKET_URL } from "@/config";
import {
  createContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import socketIOClient, { Socket } from "socket.io-client";
import { useDispatch, useSelector } from "react-redux";
import { TMemberInfos } from "@/features/profile/types";
import queryString from "query-string";
import { store, persistor } from "@/store";
import {
  getMemberInfos,
  getToken,
  updateIsOnline,
  updateMemberCurrentExchange,
  updateMemberProfile,
  updateProductTypePayment,
} from "@/store/reducers/member/member.actions";
import { axios } from "@/lib/axios";
import { apiGetUserConnected } from "@/features/auth/api/auth.api";
import { hasImpersonationExchangeStarted } from "@/features/auth/api/impersonationExchange";
import { TMemberProfile } from "@/features/auth/types";
import { Snackbar } from "@mui/material";
import { Toastify } from "@/utils/toast";
import {
  selectIsPaymentConfirm,
  selectIsWebsiteMaintenance,
} from "@/store/reducers/general_param/general_param.selector";
import { maintenanceRoutes } from "./maintenance.routes";
import {
  updateIsPaymentConfirm,
  updateIsPaymentPendingProcess,
} from "@/store/reducers/general_param/general_param.actions";
import Swal from "sweetalert2";
import { SafeErrorBoundaries } from "@/components/SafeErrorBoundaries/SafeErrorBoundaries";
import { paymentConfirmedRoutes } from "./paymentConfirmed.routes";

export const AppSocketContext = createContext<{
  socketIO: Socket | null;
  socketRef: any;
  logout: () => Promise<void>;
}>({
  socketIO: null,
  socketRef: null,
  logout: () => Promise.resolve(),
});

const AppRoutes = () => {
  const socketRef = useRef<Socket | null>(null);
  const [socketIO, setSocketIO] = useState<Socket | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasSocketError, setHasSocketError] = useState(false);

  const tokenFromStore = useSelector(selectToken);
  // Évite de rejouer le nettoyage (CLEAR_STORE / navigate) à chaque render
  // tant que l'URL contient encore le ticket, une fois qu'il a déjà été fait.
  const hasHandledStaleSessionRef = useRef(false);
  const isWebsiteInMaintainance = useSelector(selectIsWebsiteMaintenance);
  const isPaymentConfirmedFromStore = useSelector(selectIsPaymentConfirm);

  const memberInfosFromStore = useSelector(selectMemberInfos) as TMemberInfos;
  console.log({ memberInfosFromStore });

  const parsed = queryString.parse(window.location.search);
  console.log({ parsed });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const logout = useCallback(() => {
    // Retourne la promesse pour permettre aux appelants (ex: bandeau
    // d'impersonation) d'attendre la fin du nettoyage avant, par exemple,
    // de fermer l'onglet — sinon la requête de déconnexion peut être
    // annulée par le navigateur et la session côté serveur ne jamais être
    // révoquée.
    return axios
      .post("/auth-s/api/v1/auth/logout")
      .then(() => {})
      .catch(() => {})
      .finally(() => {
        store.dispatch({ type: "CLEAR_STORE" });
        localStorage.clear();
        navigate(routePaths.auth, { replace: true });
        dispatch(getToken(""));
        persistor.purge();

        if (socketRef.current) {
          socketRef.current.disconnect();
          socketRef.current = null;
        }
      });
  }, [dispatch, navigate]);

  const handleTokenExpired = useCallback(() => {
    setAttempt((prev) => prev + 1);

    axios
      .post("/auth-s/api/v1/auth/p/refresh-token")
      .then((res) => {
        const newToken = res.data;
        store.dispatch(getToken(newToken));

        initializeSocket(newToken);
      })
      .catch((err) => {
        console.log({ err });
        if (attempt >= 3) {
          logout();
          setAttempt(0);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt, logout]);

  useEffect(() => {
    const handleOnline = () => {
      console.log("🍄 Online");
      if (hasSocketError) {
        console.log("🍄 refresh launch");
        handleTokenExpired();
      }
    };
    const handleOffline = () => {
      setHasSocketError(true);
      console.log("🍄 Offline");
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [setHasSocketError, handleTokenExpired, hasSocketError]);

  const initializeSocket = useCallback(
    (token: string) => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }

      const io = socketIOClient(SOCKET_URL, {
        path: "/realtime/ws/handshake",
        query: { token: `Bearer ${token}` },
        transports: ["websocket"],
        reconnection: true,
        reconnectionAttempts: Infinity,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        autoConnect: true,
      });

      io.on("token-expired", (data) => {
        console.log("🍄 Token expired", data);
        handleTokenExpired();
      });

      io.on("connect", () => {
        console.log("🍄 Socket connected");
        if (hasSocketError) {
          handleTokenExpired();
          setHasSocketError(false);
        }
      });

      io.on("disconnect", () => {
        console.log("🍄 Socket disconnected");
        // setHasSocketError(true);
      });

      io.on("error", (err) => {
        console.log("🍄 Socket error", err);
      });

      io.on("connect_error", (err) => {
        console.log("🍄 Socket connect error", err);
        setHasSocketError(true);
      });

      socketRef.current = io;
      setSocketIO(io);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [socketRef, hasSocketError, setHasSocketError]
  );

  // const socketIO_memo = useMemo(() => {
  //   const io = socketIOClient(SOCKET_URL, {
  //     path: "/realtime/ws/handshake",
  //     query: { token: `Bearer ${tokenFromStore}` },
  //     transports: ["websocket"],
  //     reconnection: true,
  //     reconnectionAttempts: Infinity,
  //     reconnectionDelay: 1000,
  //     reconnectionDelayMax: 5000,
  //     timeout: 20000,
  //     autoConnect: true,
  //   });

  //   socketRef.current = io;

  //   return io;
  // }, [socketRef, tokenFromStore]);

  // useEffect(() => {
  //   socketIO_memo.on("token-expired", (data) => {
  //     console.log("🍄 Token expired", data);
  //     handleTokenExpired();
  //   });

  //   return () => {
  //     socketIO_memo.disconnect();
  //   };
  // }, [socketIO_memo, handleTokenExpired]);

  useEffect(() => {
    console.log("🥶IN - index");
    return () => {
      console.log("🥶OUT - index");
    };
  }, [socketIO]);

  useEffect(() => {
    if (tokenFromStore) {
      initializeSocket(tokenFromStore);
    }
  }, [tokenFromStore, initializeSocket]);

  useEffect(() => {
    if (
      memberInfosFromStore?.isVerified === false &&
      parsed?.token &&
      tokenFromStore.length > 0
    ) {
      store.dispatch({ type: "CLEAR_STORE" });
      localStorage.clear();
      dispatch(getToken(""));
      persistor.purge();

      if (parsed?.email) {
        navigate(
          routePaths.authChildren.resetPasswordAbsolute +
            "?token=" +
            parsed?.token +
            "&email=" +
            parsed?.email,
          { replace: true }
        );
      } else {
        navigate(
          routePaths.authChildren.verifEmailAbsolute +
            "?token=" +
            parsed?.token,
          { replace: true }
        );
      }
    }

    // Lien d'impersonation ouvert depuis le back-office alors qu'une session
    // (staff ou membre) traînait déjà dans ce navigateur : on l'efface pour
    // laisser la place à l'échange du ticket sur /auth/impersonate.
    //
    // `hasImpersonationExchangeStarted` distingue les deux cas où
    // `tokenFromStore` est non vide :
    //  - session ÉTRANGÈRE préexistante (échange pas encore lancé) → nettoyer ;
    //  - session issue de NOTRE PROPRE échange → surtout ne pas l'effacer.
    // On ne peut pas se fier à la valeur du token au montage : l'app n'a pas
    // de PersistGate, donc redux-persist réhydrate après le premier rendu et
    // le token apparaît « en retard ».
    if (
      parsed?.impersonation_ticket &&
      tokenFromStore.length > 0 &&
      !hasImpersonationExchangeStarted(
        parsed.impersonation_ticket as string
      ) &&
      !hasHandledStaleSessionRef.current
    ) {
      hasHandledStaleSessionRef.current = true;

      store.dispatch({ type: "CLEAR_STORE" });
      localStorage.clear();
      dispatch(getToken(""));
      persistor.purge();

      navigate(
        routePaths.authChildren.impersonateAbsolute +
          "?impersonation_ticket=" +
          parsed?.impersonation_ticket,
        { replace: true }
      );
    }

    if (parsed?.payment === "pending") {
      if (parsed?.paymentType === "boost") {
        store.dispatch(updateProductTypePayment("boost"));
      } else if (parsed?.paymentType === "mouqabala") {
        store.dispatch(updateProductTypePayment("mouqabala"));
      } else {
        store.dispatch(updateProductTypePayment(""));
      }
      store.dispatch(updateIsPaymentConfirm(true));
    }
  }, [dispatch, memberInfosFromStore, navigate, parsed, tokenFromStore]);

  useEffect(() => {
    console.log(" payment confirm ", isPaymentConfirmedFromStore);
  }, [isPaymentConfirmedFromStore]);

  // Microsoft Clarity temporarily disabled while investigating iPhone/Safari
  // reloads on long profile-list scroll. Keep this block commented so the
  // app no longer tags Clarity sessions even if the script is re-enabled.
  // useEffect(() => {
  //   const memberId = memberInfosFromStore?.memberId;
  //   const clarity = (window as any).clarity;
  //   if (memberId && typeof clarity === "function") {
  //     clarity("set", "member_id", memberId);
  //   }
  // }, [memberInfosFromStore?.memberId]);

  const routes = useCallback((): RouteObject[] => {
    if (isWebsiteInMaintainance) {
      return maintenanceRoutes;
    } else if (isPaymentConfirmedFromStore) {
      return paymentConfirmedRoutes;
    } else {
      return tokenFromStore.length === 0 ? publicRoutes : protectedRoutes;
    }
  }, [
    isWebsiteInMaintainance,
    isPaymentConfirmedFromStore,
    tokenFromStore.length,
  ]);

  const element = useRoutes([...routes()]);

  const location = useLocation();
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Enregistrer le Service Worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((registration) => {
          // Écouter les messages du Service Worker
          navigator.serviceWorker.addEventListener("message", (event) => {
            if (event.data.type === "UPDATE_AVAILABLE") {
              setUpdateAvailable(true);
            }
          });
        })
        .catch((error) =>
          console.error("Service Worker registration failed:", error)
        );
    }
  }, [location.pathname]);

  useEffect(() => {
    // updateAvailable &&
    //   Swal.fire({
    //     imageUrl: `/logo192.png`,
    //     imageHeight: 50,
    //     imageAlt: "logo",
    //     html: `
    //     <p style="color: var(--ui-medium)">Une <b style="color: var(--ui-primary)">nouvelle mise à jour</b> de l'application est disponible. Veuillez <b style="color: var(--ui-primary)">rafraichir</b> la page pour profiter pleinement de la plateforme.</p>
    //   `,
    //     showCloseButton: true,
    //     focusConfirm: false,
    //     confirmButtonText: `
    //     Rafraichir
    //   `,
    //     confirmButtonAriaLabel: "refresh",
    //   }).then((result) => {
    //     if (result.isConfirmed) {
    //       setUpdateAvailable(false);
    //       window.location.reload();
    //     }
    //     if (result.isDismissed) {
    //       setUpdateAvailable(false);
    //       window.location.reload();
    //     }
    //   });
    if (updateAvailable) {
      // window.location.reload();
    }
  }, [updateAvailable]);

  return (
    <AppSocketContext.Provider value={{ socketIO, socketRef, logout }}>
      <>
        <SafeErrorBoundaries>{element}</SafeErrorBoundaries>
        <Snackbar
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          open={false}
          message="... Paiement en cours de traitement ..."
        />
      </>
    </AppSocketContext.Provider>
  );
};

export default AppRoutes;
